/* eslint-disable no-console */
import React, { useEffect, useMemo } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import Providers from "./utils/provider";
import "./i18n/config";
import { useAuth0 } from "@auth0/auth0-react";
import Spinner from "./components/Spinner";
import Layout from "./views/Layout";
import UserList from "./pages/User/List";
import AddUser from "./pages/User/AddUser";
import EditUser from "./pages/User/EditUser";
import { useUserStore } from "./stores/user.store";
import { useMenuStore } from "./stores/menu.store";
import HelpSupportList from "./pages/HelpAndSupport/List";
import Unauthorized from "./pages/Unauthorized/Index";
import AccessDenied from "./pages/AccessDenied";
import BeneficiaryReport from "./pages/Reports/BeneficiaryReport";
import CardsList from "./pages/Cards";
import TreatmentPlans from "./pages/TreatmentPlans";
import Events from "./pages/Events";
import Actions from "./pages/Actions";
import Roles from "./pages/Roles";
import GroupLabels from "./pages/GroupLabels";
import Organizations from "./pages/Organizations";
import Accounts from "./pages/Accounts";
import Programs from "./pages/Programs";
import { UserStatus } from "./domain/user.interface";
import Callback from "./pages/Callback";
import Dashboard from "./pages/Dashboard";
import ProviderReport from "./pages/Reports/ProviderReport";
import { setToken, setTokenBo } from "./utils/axios.create";
import RewardsConfigPage from "./pages/RewardsConfiguration";
import SettingsManagement from "./pages/SettingsManagement";

const BeneficiaryList = React.lazy(() => import("./pages/Beneficiary/List"));
const BeneficiaryProfile = React.lazy(
	() => import("./pages/Beneficiary/Profile"),
);

function App() {
	const {
		loginWithRedirect,
		isAuthenticated,
		isLoading: isAuthLoading,
		error,
		user,
		getAccessTokenSilently,
	} = useAuth0();
	const {
		menus,
		userInfo,
		isLoading: isUserLoading,
		getUserByEmail,
		clearUser,
	} = useUserStore();
	const { setMenuItems } = useMenuStore();

	useEffect(() => {
		if (userInfo && menus) {
			setMenuItems(menus);
		}
	}, [userInfo, menus]);

	if (error) {
		return <div>Oops... {error.message}</div>;
	}

	useEffect(() => {
		const auth0Sub = user?.sub ? user.sub.split("|") : [];
		let email = user?.email;
		if (!email) {
			email = auth0Sub[auth0Sub.length - 1];
		}
		if (!isAuthLoading && isAuthenticated && email) {
			const getToken = async () => {
				try {
					const token = await getAccessTokenSilently();
					setToken(token);
					setTokenBo(token);
					await getUserByEmail(email as string);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (e: any) {
					if (
						e.error === "missing_refresh_token" ||
						e.error === "invalid_grant"
					) {
						loginWithRedirect();
					}
				}
			};
			getToken();
		}
	}, [
		isAuthLoading,
		isAuthenticated,
		getAccessTokenSilently,
		user?.email,
		userInfo?.email,
	]);

	useEffect(() => {
		if (!isAuthenticated && !isAuthLoading) {
			clearUser();
			localStorage.clear();
			sessionStorage.clear();
			document.cookie = ""; // Clear cookies
			loginWithRedirect();
		}
	}, [isAuthenticated, isAuthLoading]);

	const showComponent = useMemo<"spinner" | "accessDenied" | "continue">(() => {
		if (isAuthLoading || isUserLoading || !user || !userInfo) {
			return "spinner";
		} else if (userInfo && userInfo.status === UserStatus.INACTIVE) {
			return "accessDenied";
		} else {
			return "continue";
		}
	}, [isAuthLoading, isUserLoading, user, userInfo]);

	return (
		<>
			{showComponent === "spinner" ? (
				<div className="flex w-screen h-screen items-center justify-center">
					<Spinner />
				</div>
			) : (
				<Router>
					<Providers>
						{showComponent == "accessDenied" && <AccessDenied />}
						{showComponent == "continue" && (
							<Layout>
								<Routes>
									<Route path="/" element={<Dashboard />} />
									<Route path="/dashboard" element={<Dashboard />} />
									<Route path="/callback" element={<Callback />} />
									<Route path="/users" element={<UserList />} />
									<Route path="/user" element={<AddUser />} />
									<Route path="/users/:id" element={<EditUser />} />
									<Route path="/beneficiaries">
										<Route
											path="/beneficiaries"
											element={<BeneficiaryList />}
										/>
										<Route
											path="/beneficiaries/:beneficiaryId"
											element={<BeneficiaryProfile />}
										/>
									</Route>
									<Route path="/cards" element={<CardsList />} />
									<Route path="/treatmentPlans" element={<TreatmentPlans />} />
									<Route path="/events" element={<Events />} />
									<Route path="/actions" element={<Actions />} />
									<Route path="/roles" element={<Roles />} />
									<Route path="/group-labels" element={<GroupLabels />} />
									<Route path="/settings" element={<SettingsManagement />} />
									<Route path="/organizations" element={<Organizations />} />
									<Route path="/accounts" element={<Accounts />} />
									<Route path="/programs" element={<Programs />} />
									<Route
										path="/beneficiaryReport"
										element={<BeneficiaryReport />}
									/>
									<Route path="/providerReport" element={<ProviderReport />} />
									<Route
										path="/help-and-support"
										element={<HelpSupportList />}
									/>
									<Route path="/unauthorized" element={<Unauthorized />} />
									<Route
										path="/rewards-configuration"
										element={<RewardsConfigPage />}
									/>
								</Routes>
							</Layout>
						)}
					</Providers>
				</Router>
			)}
		</>
	);
}

export default App;
